import { useState, useEffect, useContext } from 'react';
import { APIContext } from '../Contexts/APIContext';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Line from '../Utilities/Line';
import Button from '@mui/material/Button';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { FormContainer, useForm } from 'react-hook-form-mui';
import DialogActions from '@mui/material/DialogActions';
import ValidatedTextField from '../Utilities/Forms/ValidatedTextField';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import DropDownViewModel from '../Utilities/ViewModels/DropDownViewModel';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

class AdminNotification {
    id?: string;
    title?: string;
    text: string = '';
    url?: string;
    userId?: string;
    organisationId?: string;
    siteId?: string;
    startTime?: string;
    endTime?: string;
    target?: string;
}

export const ManageNotifications = () => {
    const { get, post } = useContext(APIContext);
    const [notifications, setNotifications] = useState<AdminNotification[]>([]);
    const [organisationList, setOrganisationList] = useState<DropDownViewModel[]>([]);
    const [siteList, setSiteList] = useState<DropDownViewModel[]>([]);
    const [userList, setUserList] = useState<DropDownViewModel[]>([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [menuNotification, setMenuNotification] = useState<AdminNotification | null>(null);

    const formContext = useForm<AdminNotification>({
        defaultValues: new AdminNotification(),
    });
    const { reset, watch } = formContext;

    useEffect(() => {
        init();
        get(`Notification`, `GetNotificationDropdowns`, (data) => {
            setOrganisationList(data.organisations);
            setSiteList(data.sites);
            setUserList(data.users);
        });
    }, []);

    const init = async () => {
        setEditing(false);
        setOpen(false);
        get(`Notification`, `GetNotificationManagementTable`, setNotifications);
    }

    const newNotification = async () => {
        setEditing(false);
        setOpen(true);
        reset(new AdminNotification());
    }
    const editNotification = async () => {
        setEditing(true);
        setOpen(true);
        reset(menuNotification!);
    }

    const saveNotification = async (form: any) => {
        if (editing) {
            post(`Notification`, `EditNotification`, form, init);
        } else {
            post(`Notification`, `AddNotification`, form, init);
        }
    };

    const deleteNotification = async (event: any) => {
        event.stopPropagation();
        get(`Notification`, `ArchiveNotification?notificationId=${menuNotification!.id}`, init);
    };

    const handleMenuOpen = (event: any, notification: any) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuNotification(notification);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuNotification(null);
    };

    const orgId = watch('organisationId');
    const siteId = watch('siteId');
    const startTime = watch('startTime');
    const endTime = watch('endTime');

    return (
        <Grid container>
            <Grid>
                <Typography variant="h6">Notification Management</Typography>
            </Grid>
            <Grid>
                <Button size="small" onClick={newNotification}>New Notification</Button>
            </Grid>
            <Line />
            <Grid size={12}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Notification</TableCell>
                                <TableCell>Date Range</TableCell>
                                <TableCell>Target</TableCell>
                                <TableCell>Link</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {notifications.map(n => (
                                <TableRow key={n.id}>
                                    <TableCell>{n.title != undefined ? n.title : n.text}</TableCell>
                                    <TableCell>{n.startTime ? dayjs(n.startTime).format("DD/MM/YYYY") : 'Any date'} - {n.endTime ? dayjs(n.endTime).format("DD/MM/YYYY") : 'Any date'}</TableCell>
                                    <TableCell>{n.target}</TableCell>
                                    <TableCell>{n.url == null ? 'N' : 'Y'}</TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={(event) => handleMenuOpen(event, n)} aria-label="Staff actions dropdown">
                                            <MoreVertIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
            <Menu
                open={anchorEl != null}
                onClose={handleMenuClose}
                anchorEl={anchorEl}
            >
                <MenuItem onClick={(event) => deleteNotification(event)}                >
                    {"Remove"}
                </MenuItem>
                <MenuItem onClick={editNotification}>
                    {"Edit"}
                </MenuItem>
            </Menu>
            <Dialog open={open} onClose={() => setOpen(false)}>
                <FormContainer formContext={formContext} onSuccess={saveNotification}>
                    <DialogTitle>{editing ? 'Edit' : 'Add'} Notification</DialogTitle>
                    <DialogContent>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
                            <Grid container paddingTop="8px">
                                <Grid size={12}>
                                    <ValidatedTextField label="Notification Title" name="title" />
                                </Grid>
                                <Grid size={12}>
                                    <ValidatedTextField label="Notification Text" name="text" multiline required />
                                </Grid>
                                <Grid size={12}>
                                    <ValidatedTextField label="Link URL" name="url" />
                                </Grid>
                                <Grid size={4}>
                                    <Autocomplete
                                        options={organisationList}
                                        renderInput={(params) => <TextField {...params} label="Organisation" />}
                                        onChange={(event, value) => {
                                            formContext.setValue('organisationId', value?.id);
                                        }}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Autocomplete
                                        options={siteList.filter(f => f.data?.organisation == orgId)}
                                        renderInput={(params) => <TextField {...params} label="Site" />}
                                        onChange={(event, value) => {
                                            formContext.setValue('siteId', value?.id);
                                        }}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Autocomplete
                                        options={userList.filter(f => f.data?.organisation == orgId && (siteId == null || f.data?.site == siteId))}
                                        renderInput={(params) => <TextField {...params} label="Users" />}
                                        onChange={(event, value) => {
                                            formContext.setValue('userId', value?.id);
                                        }}
                                    />
                                </Grid>

                                <Grid size={6}>
                                    <DatePicker
                                        label="Start Date"
                                        slotProps={{ field: { clearable: true }, textField: { size: 'small' } }}
                                        format="DD/MM/YYYY"
                                        value={startTime ? dayjs(startTime) : null}
                                        onChange={(newValue) => {
                                            formContext.setValue('startTime', newValue ? newValue.format('YYYY-MM-DD') : undefined);
                                        }}
                                        minDate={dayjs().startOf('day')}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <DatePicker
                                        label="End Date"
                                        slotProps={{ field: { clearable: true }, textField: { size: 'small' } }}
                                        format="DD/MM/YYYY"
                                        value={endTime ? dayjs(endTime) : null}
                                        onChange={(newValue) => {
                                            formContext.setValue('endTime', newValue ? newValue.format('YYYY-MM-DD') : undefined);
                                        }}
                                        minDate={startTime ? dayjs(startTime) : dayjs().startOf('day')}
                                    />
                                </Grid>
                            </Grid>
                        </LocalizationProvider>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={init}>Cancel</Button>
                        <Button type="submit">Save</Button>
                    </DialogActions>
                </FormContainer>
            </Dialog>
        </Grid>
    );
};
